/**
 * The workspace's tabs, and the one word each travels under in the URL (D9, D10).
 *
 * FOUR TABS FOR EVERY AUTOMATION, A FIFTH FOR THE ONES THAT SEND E-MAIL (D10).
 * Allgemein, Auslöser, Ausführungen and Protokoll are the same for every
 * automation. "E-Mail-Vorlage" is added — second, beside the general facts —
 * exactly for an automation whose `action_type` is `send_email`. A
 * tenant-data automation such as the move-in address never gets one (SoT §3.3,
 * D8-T): it sends no mail and has no template.
 *
 * WHY `action_type`, AND NOT THE CATEGORY OR A CAPABILITY. The tab has to be
 * decided before anything about the template is loaded — a mount, and every
 * tab but this one, must not ask for it — so it can only come from the
 * automation row the page already holds. `action_type` is the registry's own
 * field on that row, and the backend pins it to the template catalogue: every
 * `send_email` automation has a template profile and nothing else has one
 * (`test_every_email_automation_has_a_profile_and_nothing_else_does`). The
 * `category` is a filter grouping, not that contract. Whether the template may
 * be EDITED is a different question with a different, server-authoritative
 * answer (`capabilities.custom_authoring` of the template itself), decided in
 * the tab and never here.
 *
 * THE URL VALUE IS THE TAB'S ID. `?tab=executions` names the tab whose id is
 * `executions`, so there is no second table translating between them that could
 * drift. The values are English because they are an address, not a word a
 * reader is shown; the labels are the German the reader sees.
 *
 * EXACT AND CASE-SENSITIVE, like `useInvoiceView`: nothing in the product writes
 * `"Protocol"`, and accepting it would only be guessing at intent. `template` is
 * a known value for every automation; whether the shown automation HAS that tab
 * is the page's question (`isWorkspaceTabAvailable`).
 */

export const GENERAL_TAB = "general";
export const TEMPLATE_TAB = "template";
export const TRIGGER_TAB = "trigger";
export const EXECUTIONS_TAB = "executions";
export const PROTOCOL_TAB = "protocol";

/** The tab shown when nothing usable was asked for. */
export const DEFAULT_WORKSPACE_TAB = GENERAL_TAB;

/** The registry's action that sends e-mail — the one that has a template. */
export const SEND_EMAIL_ACTION = "send_email";

const GENERAL = { id: GENERAL_TAB, label: "Allgemein" };
// `lazyMount`: the template, the runs and the timeline each cost requests, and
// a tab nobody opened must not cost one.
const TEMPLATE = { id: TEMPLATE_TAB, label: "E-Mail-Vorlage", lazyMount: true };
const TRIGGER = { id: TRIGGER_TAB, label: "Auslöser" };
const EXECUTIONS = { id: EXECUTIONS_TAB, label: "Ausführungen", lazyMount: true };
const PROTOCOL = { id: PROTOCOL_TAB, label: "Protokoll", lazyMount: true };

const BASE_TABS = Object.freeze([GENERAL, TRIGGER, EXECUTIONS, PROTOCOL]);
const EMAIL_TABS = Object.freeze([GENERAL, TEMPLATE, TRIGGER, EXECUTIONS, PROTOCOL]);

/** Every value `?tab=` may carry, for any automation. */
export const AUTOMATION_WORKSPACE_TAB_IDS = EMAIL_TABS.map((tab) => tab.id);

/** Whether this automation has the e-mail template workspace. */
export function hasTemplateWorkspace(automation) {
  return automation?.action_type === SEND_EMAIL_ACTION;
}

/** This automation's tabs, in display order. */
export function workspaceTabsFor(automation) {
  return hasTemplateWorkspace(automation) ? EMAIL_TABS : BASE_TABS;
}

/** Whether `tab` is one of this automation's tabs. */
export function isWorkspaceTabAvailable(tab, automation) {
  return workspaceTabsFor(automation).some((entry) => entry.id === tab);
}

/** A raw `?tab=` value, or `null` if it names no tab at all. */
export function normalizeWorkspaceTab(raw) {
  return AUTOMATION_WORKSPACE_TAB_IDS.includes(raw) ? raw : null;
}
